import { useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';

import { useAuth0Context } from '#src/state/contexts/auth0-context.js';
import { blobToDataUrl } from '#utils/binary-data-url.js';
import {
	persistentQueryOptions,
	persistentQueryPolicies,
} from '#utils/persistent-query-cache/persistent-query-cache.js';

/**
 * Resolves authenticated project-model render asset references to cacheable data URLs.
 *
 * The resolver is backed by TanStack Query so repeated renders of the same
 * `{ projectId, buildId, imageRef }` reuse memory and IndexedDB cache entries.
 */
export function useProjectModelImageUrlResolver(projectId: string, buildId: string) {
	const auth0 = useAuth0Context();
	const queryClient = useQueryClient();

	return useCallback(
		async (imageRef: string) => {
			return queryClient.fetchQuery({
				queryKey: ['project-model-render-asset-image', projectId, buildId, imageRef] as const,
				queryFn: async () => {
					const token = await auth0.client.getTokenSilently();
					const res = await fetch(`/api/v1/projects/${projectId}/builds/${buildId}/assets/${imageRef}`, {
						headers: { Authorization: `Bearer ${token}` },
					});

					if (!res.ok) {
						throw new Error(`Failed to fetch project model image asset ${imageRef}: ${res.status} ${res.statusText}`);
					}

					return blobToDataUrl(await res.blob());
				},
				...persistentQueryOptions(persistentQueryPolicies.rendererAssetImage),
			});
		},
		[projectId, buildId, auth0.client, queryClient],
	);
}
